const OnboardingAnalytics = typeof require === 'function' ? require('./onboarding_analytics') : {};
if (!OnboardingAnalytics.createWebOnboardingAnalytics) {
  // The funnel goes dark silently without this: make a bundler regression visible.
  console.warn('[onboarding] onboarding_analytics module unavailable; no onboarding_step events will be captured');
}

// Onboarding script initializing
document.addEventListener('DOMContentLoaded', function () {
  // Only initialize if on onboarding page
  const onboardingContainer = document.querySelector('.onboarding-desktop-container');
  if (!onboardingContainer) {
    return; // Not on onboarding page, exit early
  }

  // DOM Content Loaded - Starting onboarding app
  const onboardingApp = {
    currentStep: 0,
    totalSteps: 10,
    data: {
      firstName: '',
      learningLanguage: '',
      nativeLanguage: '',
      languageLevel: '',
      motivation: '',
      interests: [], // predefined interest keys from ChatForm::CHAT_INTERESTS
      customInterests: [], // user-entered interests that aren't predefined
      practiceFrequency: '',
      practiceTimeGoal: '',
      acquisitionSource: '',
      preferredDialect: '',
      coachData: null
    },

    // Audio state
    currentAudio: null,
    currentAudioRequestController: null,
    isAiCoachMode: false,
    coachIntroAdvanceReason: null,

    // Navigation state for auto-progression
    isNavigatingBackward: false,

    // Debounce state to prevent double auto-progression
    autoProgressionTimer: null,

    // Load translated dialects from data attribute, with English fallback
    languageDialects: (() => {
      const container = document.querySelector('.onboarding-desktop-container');
      const fromData = container && container.dataset.languageDialects ? JSON.parse(container.dataset.languageDialects) : null;
      if (fromData) return fromData;
      return {
        'english': [
          ['American English', 'american_english'],
          ['British English', 'british_english']
        ],
        'french': [
          ['French (France)', 'french_france'],
          ['French (Canadian)', 'french_canada']
        ],
        'portuguese': [
          ['Brazilian Portuguese', 'brazilian_portuguese'],
          ['European Portuguese', 'european_portuguese']
        ],
        'spanish': [
          ['Latin American Spanish', 'latin_american_spanish'],
          ['Castilian Spanish (Spain)', 'castilian_spanish']
        ],
        'dutch': [
          ['Dutch', 'dutch'],
          ['Flemish', 'flemish']
        ]
      };
    })(),

    isDialectPage: false,
    isArabicWarningPage: false,

    init() {
      // Initialize onboarding app
      if (OnboardingAnalytics.createWebOnboardingAnalytics) {
        this.analytics = OnboardingAnalytics.createWebOnboardingAnalytics({
          onboardingVariant: 'web_revamped_v1',
          capture: (eventName, properties, options) => {
            if (typeof window === 'undefined' || !window.posthog || typeof window.posthog.capture !== 'function') {
              return;
            }

            try {
              window.posthog.capture(eventName, properties, options);
            } catch (error) {
              console.error('Failed to capture onboarding analytics event:', error);
            }
          }
        });
      }
      this.bindEvents();
      this.restoreAnswers();
      this.bindMobileKeyboardHandling();
      this.updateProgress();
      this.updateNavigation();
      this.trackOnboardingStepTransition();
    },

    restoreAnswers() {
      const answers = JSON.parse(onboardingContainer.dataset.recoveryAnswers || '{}');
      if (!Object.keys(answers).length) return;
      // Restore only wizard fields; never execute selection handlers, which can
      // advance the wizard or submit automatically.
      Object.keys(this.data).filter(key => key !== 'coachData').forEach(key => {
        if (Object.prototype.hasOwnProperty.call(answers, key)) {
          this.data[key] = answers[key] == null ? (Array.isArray(this.data[key]) ? [] : '') : answers[key];
        }
      });
      document.getElementById('firstName').value = this.data.firstName;
      const radios = { learning_language_radio: 'learningLanguage', native_language_radio: 'nativeLanguage', language_level_radio: 'languageLevel' };
      Object.entries(radios).forEach(([name, key]) => {
        document.querySelectorAll(`input[name="${name}"]`).forEach(input => { input.checked = input.value === this.data[key]; });
      });
      document.querySelectorAll('.motivation-option').forEach(option => {
        const key = { frequency: 'practiceFrequency', duration: 'practiceTimeGoal', source: 'acquisitionSource' }[option.dataset.type] || 'motivation';
        option.classList.toggle('selected', option.dataset.value === this.data[key]);
      });
      document.querySelectorAll('.interest-tag').forEach(tag => {
        tag.classList.toggle('selected', this.data.interests.includes(tag.dataset.value));
      });
      const interests = document.querySelector('.interests-container');
      this.data.customInterests.forEach(value => {
        const tag = document.createElement('div');
        tag.className = 'interest-tag custom-interest selected';
        tag.dataset.value = value;
        tag.textContent = value;
        tag.addEventListener('click', () => this.toggleInterest(tag));
        interests.appendChild(tag);
      });
    },

    trackOnboardingStepTransition() {
      if (!this.analytics) return;
      this.analytics.trackTransition(this);
    },

    bindEvents() {
      // Bind event handlers

      // Navigation - bind to all buttons with these IDs across all steps
      document.querySelectorAll('#next-btn').forEach(nextBtn => {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.nextStep();
        });
      });

      document.querySelectorAll('#back-btn').forEach(backBtn => {
        backBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.prevStep();
        });
      });

      document.querySelectorAll('#finish-btn').forEach(finishBtn => {
        finishBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.finish();
        });
      });

      // Skip coach button
      document.querySelectorAll('#skip-coach-btn').forEach(skipBtn => {
        skipBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.skipCoachIntroduction();
        });
      });

      // First name input
      const firstNameInput = document.getElementById('firstName');
      if (firstNameInput) {
        firstNameInput.addEventListener('input', (e) => {
          this.data.firstName = e.target.value.trim();
          this.updateNextButtonState();
        });
        firstNameInput.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;

          e.preventDefault();
          this.data.firstName = e.target.value.trim();

          if (this.data.firstName.length > 0) {
            this.nextStep();
          } else {
            this.updateNextButtonState();
          }
        });
      }

      // Language selection
      document.querySelectorAll('input[name="learning_language_radio"]').forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            this.data.learningLanguage = e.target.value;
            this.data.preferredDialect = ''; // Reset dialect selection

            // Check if this language has dialects
            if (this.languageDialects[this.data.learningLanguage]) {
              this.showDialectSelection();
            } else if (this.data.learningLanguage === 'arabic') {
              // Show Arabic compatibility heads-up and require user to press Continue
              this.showArabicWarning();
            } else {
              // Ensure we're not in dialect mode for languages without dialects
              this.hideDialectSelection();
              this.hideArabicWarning();
              this.updateNextButtonState();

              // Auto-progress for languages without dialects (like mobile app)
              if (!this.isNavigatingBackward) {
                setTimeout(() => {
                  this.nextStep();
                }, 300);
              }
            }
          }
        });
      });

      // Language card click handlers - trigger radio button selection
      document.querySelectorAll('.language-card').forEach(card => {
        card.addEventListener('click', () => {
          const radioButton = card.querySelector('input[name="learning_language_radio"]');
          if (radioButton) {
            radioButton.checked = true;
            // Trigger the change event programmatically to fire auto-progression
            radioButton.dispatchEvent(new Event('change'));
          }
        });
      });

      // Dialect selection - handle both clicks and radio changes
      document.addEventListener('click', (e) => {
        if (e.target.closest('.dialect-option')) {
          this.selectDialect(e.target.closest('.dialect-option'));
        }
      });

      // Also listen for direct radio button changes on dialect selection
      document.addEventListener('change', (e) => {
        if (e.target.name === 'dialect_radio') {
          this.data.preferredDialect = e.target.value;
          this.updateNextButtonState();

          // Auto-progress after dialect selection
          setTimeout(() => {
            this.nextStep();
          }, 300);
        }
      });

      // Native language selection
      document.querySelectorAll('.native-language-option').forEach(option => {
        option.addEventListener('click', () => {
          this.selectNativeLanguage(option);
        });
      });

      // Native language radio button selection
      document.querySelectorAll('input[name="native_language_radio"]').forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            this.data.nativeLanguage = e.target.value;
            this.updateNextButtonState();

            // Auto-progress after native language selection (like mobile app)
            this.scheduleAutoProgression(300, 'native-language-radio');
          }
        });
      });

      // Level selection
      document.querySelectorAll('input[name="language_level_radio"]').forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (e.target.checked) {
            this.data.languageLevel = e.target.value;
            this.updateNextButtonState();

            // Auto-progress after language level selection (like mobile app)
            if (!this.isNavigatingBackward) {
              setTimeout(() => {
                this.nextStep();
              }, 300);
            }
          }
        });
      });

      // Level option click handlers - trigger radio button selection
      document.querySelectorAll('.level-option').forEach(option => {
        option.addEventListener('click', () => {
          const radioButton = option.querySelector('input[name="language_level_radio"]');
          if (radioButton) {
            radioButton.checked = true;
            // Trigger the change event programmatically to fire auto-progression
            radioButton.dispatchEvent(new Event('change'));
          }
        });
      });

      // Motivation selection (only for step 5)
      document.querySelectorAll('.motivation-option:not([data-type])').forEach(option => {
        option.addEventListener('click', () => {
          this.selectMotivation(option);
        });
      });

      // Interest selection
      document.querySelectorAll('.interest-tag').forEach(tag => {
        tag.addEventListener('click', () => {
          this.toggleInterest(tag);
        });
      });

      // Custom interest
      const customInput = document.querySelector('.custom-interest-input');
      const addBtn = document.querySelector('.add-interest-btn');

      if (addBtn && customInput) {
        addBtn.addEventListener('click', () => this.addCustomInterest());
        customInput.addEventListener('input', () => this.updateCustomInterestButtonState());
        customInput.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;

          e.preventDefault();
          e.stopPropagation();
          this.addCustomInterest();
        });
        this.updateCustomInterestButtonState();
      }

      // Practice options (now using motivation-option class with data-type attributes)
      document.querySelectorAll('.motivation-option[data-type]').forEach(option => {
        option.addEventListener('click', () => {
          this.selectPracticeOption(option);
        });
      });

    },

    bindMobileKeyboardHandling() {
      const keyboardSensitiveInputs = [
        document.getElementById('firstName'),
        document.querySelector('.custom-interest-input')
      ].filter(Boolean);

      keyboardSensitiveInputs.forEach(input => {
        input.addEventListener('focus', () => this.handleKeyboardInputFocus(input));
        input.addEventListener('blur', () => this.handleKeyboardInputBlur());
      });

      if (typeof window === 'undefined') return;

      const refreshInset = () => this.updateMobileKeyboardInset();
      if (window.visualViewport && window.visualViewport.addEventListener) {
        window.visualViewport.addEventListener('resize', refreshInset);
        window.visualViewport.addEventListener('scroll', refreshInset);
      } else if (window.addEventListener) {
        window.addEventListener('resize', refreshInset);
      }
    },

    onboardingContainer() {
      return document.querySelector('.onboarding-desktop-container');
    },

    handleKeyboardInputFocus(input) {
      const container = this.onboardingContainer();
      if (container) container.classList.add('is-keyboard-focused');

      this.updateMobileKeyboardInset();
      this.scrollInputIntoKeyboardSafeView(input);

      // Mobile browsers often report the keyboard viewport after the focus event.
      setTimeout(() => this.scrollInputIntoKeyboardSafeView(input), 250);
    },

    handleKeyboardInputBlur() {
      setTimeout(() => {
        const active = document.activeElement;
        if (active && (active.id === 'firstName' || active.classList && active.classList.contains('custom-interest-input'))) {
          return;
        }

        const container = this.onboardingContainer();
        if (container) {
          container.classList.remove('is-keyboard-focused');
          container.style.setProperty('--onboarding-keyboard-inset', '0px');
        }
      }, 100);
    },

    updateMobileKeyboardInset() {
      const container = this.onboardingContainer();
      if (!container || typeof window === 'undefined' || !window.visualViewport) return;

      const viewport = window.visualViewport;
      const keyboardInset = Math.max(0, window.innerHeight - viewport.height - (viewport.offsetTop || 0));
      container.style.setProperty('--onboarding-keyboard-inset', `${Math.round(keyboardInset)}px`);
    },

    scrollInputIntoKeyboardSafeView(input) {
      if (!input || !input.scrollIntoView) return;

      input.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    },

    stopCurrentAudio() {
      if (this.currentAudioRequestController) {
        this.currentAudioRequestController.abort('navigation');
        this.currentAudioRequestController = null;
      }

      if (this.currentAudio) {
        this.currentAudio.pause();

        // Remove all event listeners to prevent memory leaks
        if (this.currentAudio._playHandler) {
          this.currentAudio.removeEventListener('play', this.currentAudio._playHandler);
        }
        if (this.currentAudio._playTrackingHandler) {
          this.currentAudio.removeEventListener('play', this.currentAudio._playTrackingHandler);
        }
        if (this.currentAudio._endedHandler) {
          this.currentAudio.removeEventListener('ended', this.currentAudio._endedHandler);
        }
        if (this.currentAudio._errorHandler) {
          this.currentAudio.removeEventListener('error', this.currentAudio._errorHandler);
        }

        this.currentAudio = null;

        // Clean up speaking states
        const introElement = document.getElementById('coach-introduction');
        if (introElement) {
          introElement.classList.remove('speaking');
        }

        // Clean up any text elements that might be speaking
        const greetingEl = document.getElementById('coach-greeting');
        if (greetingEl) {
          greetingEl.classList.remove('speaking');
        }
      }
    },

    createAudioRequestController() {
      if (this.currentAudioRequestController) {
        this.currentAudioRequestController.abort('superseded');
      }

      const controller = new AbortController();
      this.currentAudioRequestController = controller;
      return controller;
    },

    clearAudioRequestController(controller) {
      if (this.currentAudioRequestController === controller) {
        this.currentAudioRequestController = null;
      }
    },

    expectedAudioAbort(error) {
      return error && error.name === 'AbortError';
    },

    markCoachIntroAudioFailed() {
      if (this.currentStep === 3 && !this.coachIntroAdvanceReason) {
        this.coachIntroAdvanceReason = 'audio_failed';
      }
    },

    async nextStep() {

      if (!this.canProceed()) {
        return;
      }

      if (this.currentStep === 3 && !this.coachIntroAdvanceReason) {
        this.coachIntroAdvanceReason = 'skip_tapped';
      }

      // Stop any current audio when navigating
      this.stopCurrentAudio();

      // Special handling for step 1 (language selection with dialect)
      if (this.currentStep === 1 && !this.isDialectPage) {
        // If language has dialects, show dialect selection instead of moving to next step
        if (this.languageDialects[this.data.learningLanguage]) {
          this.showDialectSelection();
          return;
        }
      }

      // If we're on dialect page, hide it and proceed
      if (this.isDialectPage) {
        this.hideDialectSelection();
      }

      // If we're on Arabic warning page, hide it and proceed
      if (this.isArabicWarningPage) {
        this.hideArabicWarning();
      }

      // Special handling for step 2 (native language) - fetch coach data
      if (this.currentStep === 2) {
        await this.fetchCoachData();
      }

      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep = this.nextStepIndex();
        this.showStep();
        this.updateProgress();
        this.updateNavigation();

        // Show coach introduction after moving to step 3
        if (this.currentStep === 3) {
          this.showCoachIntroduction();
          // Enable AI coach mode after introduction
          this.isAiCoachMode = true;
        }

        // Generate voice lines for steps 4-9 (after coach intro)
        if (this.isAiCoachMode && this.data.coachData && this.currentStep >= 4 && this.currentStep <= 9) {
          setTimeout(() => {
            this.playStepVoiceLine();
          }, 500); // Small delay to let step transition complete
        }
      }
    },

    prevStep() {
      // Stop any current audio when navigating
      this.stopCurrentAudio();

      // Clear any pending auto-progression
      if (this.autoProgressionTimer) {
        clearTimeout(this.autoProgressionTimer);
        this.autoProgressionTimer = null;
      }

      // Set backward navigation flag to prevent auto-progression
      this.isNavigatingBackward = true;

      // Reset flag after a short delay
      setTimeout(() => {
        this.isNavigatingBackward = false;
      }, 500);

      // If we're on dialect page, go back to language selection
      if (this.isDialectPage) {
        this.hideDialectSelection();
        this.data.preferredDialect = '';
        this.updateNextButtonState();
        this.trackOnboardingStepTransition();
        return;
      }

      // If we're on Arabic warning page, go back to language selection
      if (this.isArabicWarningPage) {
        this.hideArabicWarning();
        this.updateNextButtonState();
        return;
      }

      if (this.currentStep > 0) {
        this.currentStep = this.previousStepIndex();
        this.showStep();
        this.updateProgress();
        this.updateNavigation();
      }
    },

    showStep() {
      if (this.currentStep === 3) {
        this.coachIntroAdvanceReason = null;
      }

      document.querySelectorAll('.step').forEach((step, index) => {
        step.classList.toggle('active', index === this.currentStep);
      });

      // If we're returning to step 1, ensure proper language selection display
      if (this.currentStep === 1 && this.isDialectPage) {
        this.hideDialectSelection();
      }

      // Shuffle acquisition source options when showing step 9
      if (this.currentStep === 9) {
        this.shuffleAcquisitionSources();
      }

      this.trackOnboardingStepTransition();
    },

    updateProgress() {
      // Endowed-progress easing: start part-filled and fill fastest early,
      // so the bar reads further along than the raw step count (e.g. step 5
      // of 10 shows ~61%). Research on survey progress indicators shows
      // fast-early progress reduces abandonment while slow-early increases it.
      const fraction = this.currentStep / (this.totalSteps - 1);
      const displayed = Math.min(100, Math.round(10 + 90 * Math.pow(fraction, 0.7)));
      const progressFill = document.getElementById('progress-fill');
      if (progressFill) progressFill.style.width = displayed + '%';

      const stepCounter = document.getElementById('current-step');
      if (stepCounter) stepCounter.textContent = this.currentStep + 1;
    },

    updateNavigation() {
      // Update all back buttons
      document.querySelectorAll('#back-btn').forEach(backBtn => {
        backBtn.style.display = this.currentStep > 0 ? 'flex' : 'none';
      });

      // Update all next buttons
      document.querySelectorAll('#next-btn').forEach(nextBtn => {
        if (this.currentStep === this.totalSteps - 1) {
          nextBtn.style.display = 'none';
        } else if (this.currentStep === 3) {
          // Step 3 (coach introduction) - initially hide next button, controlled by audio completion
          nextBtn.style.display = 'none';
        } else {
          nextBtn.style.display = 'flex';
        }
      });

      // Update all skip buttons - only show on coach introduction step
      document.querySelectorAll('#skip-coach-btn').forEach(skipBtn => {
        if (this.currentStep === 3) {
          skipBtn.style.display = 'inline-flex';
        } else {
          skipBtn.style.display = 'none';
        }
      });

      // Update all finish buttons
      document.querySelectorAll('#finish-btn').forEach(finishBtn => {
        if (this.currentStep === this.totalSteps - 1) {
          finishBtn.style.display = 'flex';
        } else {
          finishBtn.style.display = 'none';
        }
      });

      this.updateNextButtonState();
    },

    updateNextButtonState() {
      const canProceed = this.canProceed();
      const isSkippingInterests = this.currentStep === 6 && !this.hasMinimumInterests();

      // Update all next buttons
      document.querySelectorAll('#next-btn').forEach(nextBtn => {
        nextBtn.disabled = !canProceed;
        nextBtn.classList.toggle('disabled', !canProceed);
        this.updateNextButtonLabel(nextBtn, isSkippingInterests);
      });

      // Update all finish buttons
      document.querySelectorAll('#finish-btn').forEach(finishBtn => {
        finishBtn.disabled = !canProceed;
        finishBtn.classList.toggle('disabled', !canProceed);
      });
    },

    updateNextButtonLabel(nextBtn, useSkipLabel) {
      const label = useSkipLabel
        ? (nextBtn.dataset.skipLabel || 'Skip')
        : (nextBtn.dataset.continueLabel || 'Continue');

      nextBtn.innerHTML = `
        <span class="next-btn-label">${label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="m9 18 6-6-6-6"/>
        </svg>
      `;
    },

    hasMinimumInterests() {
      return (this.data.interests.length + this.data.customInterests.length) >= 3;
    },

    isBeginnerLevel() {
      return this.data.languageLevel === 'beginner';
    },

    nextStepIndex() {
      // Beginner learners skip the Interests step (index 6) because interests
      // are not used until higher levels. Keep this in sync if steps move.
      if (this.currentStep === 5 && this.isBeginnerLevel()) {
        return 7;
      }

      return this.currentStep + 1;
    },

    previousStepIndex() {
      // Mirror the beginner-only step 6 skip when navigating back from practice.
      if (this.currentStep === 7 && this.isBeginnerLevel()) {
        return 5;
      }

      return this.currentStep - 1;
    },

    canProceed() {
      switch (this.currentStep) {
        case 0: return this.data.firstName.length > 0;
        case 1:
          if (this.isDialectPage) {
            return this.data.preferredDialect.length > 0;
          } else {
            return this.data.learningLanguage.length > 0;
          }
        case 2: return this.data.nativeLanguage.length > 0;
        case 3: return true; // Coach introduction step
        case 4: return this.data.languageLevel.length > 0;
        case 5: return this.data.motivation.length > 0;
        case 6: return true; // Zero interests is allowed when the user taps Skip.
        case 7: return this.data.practiceFrequency.length > 0;
        case 8: return this.data.practiceTimeGoal.length > 0;
        case 9: return this.data.acquisitionSource.length > 0;
        default: return false;
      }
    },

    showDialectSelection() {
      this.isDialectPage = true;
      const languageSections = document.querySelectorAll('.language-section');
      const dialectSelection = document.getElementById('dialect-selection');
      const dialectOptions = document.getElementById('dialect-options');
      const learningLanguagePrompt = document.getElementById('learning-language-prompt');

      // Hide both language selection sections, show dialect selection
      languageSections.forEach(section => section.style.display = 'none');
      if (learningLanguagePrompt) learningLanguagePrompt.style.display = 'none';
      dialectSelection.style.display = 'block';

      // Populate dialect options
      dialectOptions.innerHTML = '';
      const dialects = this.languageDialects[this.data.learningLanguage];

      dialects.forEach(([label, value]) => {
        const dialectDiv = document.createElement('div');
        dialectDiv.className = 'language-card dialect-option';
        dialectDiv.dataset.value = value;
        dialectDiv.dataset.label = label;

        // Get appropriate flag for dialect
        const flagCode = this.getDialectFlag(value);

        // Create the exact same structure as learning language cards
        dialectDiv.innerHTML = `
          <input class="form-check-input" type="radio" name="dialect_radio" id="dialect_${value}" value="${value}">
          <label class="language-card-label" for="dialect_${value}">
            <div class="language-flag">
              ${this.renderFlag(flagCode)}
            </div>
            <div class="language-name">${label}</div>
          </label>
        `;

        dialectOptions.appendChild(dialectDiv);
      });

      this.updateNextButtonState();
      this.trackOnboardingStepTransition();
    },

    showArabicWarning() {
      this.isArabicWarningPage = true;
      const languageSections = document.querySelectorAll('.language-section');
      const arabicWarning = document.getElementById('arabic-warning');

      // Hide language selection sections, show Arabic warning
      languageSections.forEach(section => section.style.display = 'none');
      if (arabicWarning) arabicWarning.style.display = 'block';

      this.updateNextButtonState();
    },

    getDialectFlag(dialectValue) {
      // Map dialect values to appropriate country flags (matching mobile app)
      const dialectFlags = {
        'american_english': 'us',
        'british_english': 'gb',
        'australian_english': 'au',
        'french_france': 'fr',
        'french_canada': 'ca',
        'brazilian_portuguese': 'br',
        'european_portuguese': 'pt',
        'latin_american_spanish': 'mx',
        'castilian_spanish': 'es',
        'dutch': 'nl',
        'flemish': 'be',
        'german': 'de',
      };

      return dialectFlags[dialectValue] || 'us';
    },

    renderFlag(countryCode) {
      // Create flag HTML using the same fi fi-<country> classes as Rails partial
      return `<span class="fi fi-${countryCode}" title="${countryCode.toUpperCase()} flag"></span>`;
    },

    hideDialectSelection() {
      this.isDialectPage = false;
      const languageSections = document.querySelectorAll('.language-section');
      const dialectSelection = document.getElementById('dialect-selection');
      const learningLanguagePrompt = document.getElementById('learning-language-prompt');

      // Show both language selection sections, hide dialect selection
      languageSections.forEach(section => section.style.display = '');
      if (learningLanguagePrompt) learningLanguagePrompt.style.display = '';
      dialectSelection.style.display = 'none';
    },

    hideArabicWarning() {
      this.isArabicWarningPage = false;
      const languageSections = document.querySelectorAll('.language-section');
      const arabicWarning = document.getElementById('arabic-warning');

      // Show language selection sections, hide Arabic warning
      languageSections.forEach(section => section.style.display = '');
      if (arabicWarning) arabicWarning.style.display = 'none';
    },

    selectDialect(option) {
      // Find the radio button within the clicked card and check it
      const radioButton = option.querySelector('input[type="radio"]');
      if (radioButton) {
        radioButton.checked = true;
        this.data.preferredDialect = radioButton.value;
        this.updateNextButtonState();

        // Auto-progress after dialect selection (like mobile app)
        setTimeout(() => {
          this.nextStep();
        }, 300);
      }
    },

    async fetchCoachData() {
      // Fetch coach data for selected language
      document.getElementById('coach-loading').style.display = 'block';

      try {
        // Use the web onboarding endpoint which uses session-based auth
        const language = this.data.learningLanguage;
        const dialect = this.data.preferredDialect;
        let url = `/account/onboarding/langua_details/coach_info?language=${encodeURIComponent(language)}`;
        if (dialect) {
          url += `&dialect=${encodeURIComponent(dialect)}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin'
        });

        if (response.ok) {
          const data = await response.json();

          if (data.coach) {
            this.data.coachData = data.coach;
          } else {
            throw new Error('No coach data in response');
          }
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to get coach info: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.error('Failed to fetch coach data:', error.message);

        // Show user-friendly error message for coach loading failure
        this.showErrorMessage(I18n.t('javascript.onboarding.coach_load_error'));

        // Use fallback coaches matching mobile app logic
        const fallbackCoaches = {
          spanish: { name: 'Maria', voice_key: 'maria', avatar_url: 'https://cdn.languastream.com/Avatars/f1.jpg' },
          english: { name: 'Lucy', voice_key: 'lucy', avatar_url: 'https://cdn.languastream.com/Avatars/f1.jpg' },
          french: { name: 'Gaelle', voice_key: 'gaelle', avatar_url: 'https://cdn.languastream.com/Avatars/f15.jpg' },
          german: { name: 'Stefan', voice_key: 'stefan', avatar_url: 'https://cdn.languastream.com/Avatars/m34.jpg' },
          italian: { name: 'Francesco', voice_key: 'francesco', avatar_url: 'https://cdn.languastream.com/Avatars/m1.jpg' }
        };

        const fallback = fallbackCoaches[this.data.learningLanguage] || fallbackCoaches.spanish;

        this.data.coachData = {
          name: fallback.name,
          avatar_url: fallback.avatar_url,
          voice_key: fallback.voice_key,
          language: this.data.learningLanguage
        };
      }

    },

    showCoachIntroduction() {
      const loadingEl = document.getElementById('coach-loading');
      const introEl = document.getElementById('coach-introduction');
      const avatarEl = document.getElementById('coach-avatar');
      const greetingEl = document.getElementById('coach-greeting');
      const skipBtn = document.getElementById('skip-coach-btn');
      const continueBtn = document.getElementById('next-btn');

      if (this.data.coachData) {
        // Update avatar
        avatarEl.src = this.data.coachData.avatar_url || '/images/default-coach-avatar.png';

        // Create greeting message
        const firstName = this.data.firstName || 'there';
        const coachName = this.data.coachData.name || 'Alex';
        const greeting = I18n.t('javascript.onboarding.coach_greeting', { firstName: firstName, coachName: coachName });

        // Hide loading, show introduction
        loadingEl.style.display = 'none';
        introEl.style.display = 'block';

        // Show skip button, hide continue button initially
        skipBtn.style.display = 'inline-flex';
        continueBtn.style.display = 'none';

        // Clear greeting element and add loading state
        greetingEl.innerHTML = '';
        greetingEl.classList.add('loading');

        // Generate and play coach audio first, text animation will start when audio plays
        // Don't await - let the audio play in background and handle completion via event listeners
        this.generateAndPlayCoachAudio(greeting, this.getCoachVoiceLanguage(), greetingEl).catch(error => {
          this.markCoachIntroAudioFailed();
          console.error('Coach audio generation failed:', error.message);
          // Show user-friendly error message for TTS failure
          this.showErrorMessage(I18n.t('javascript.onboarding.audio_unavailable'));
          // Remove loading state and fallback to text animation if audio generation fails
          greetingEl.classList.remove('loading');
          this.animateText(greetingEl, greeting);
          // Show continue button if audio generation fails entirely
          skipBtn.style.display = 'none';
          continueBtn.style.display = 'inline-flex';
        });
      }
    },

    animateText(element, text) {
      // Add speaking class for visual feedback
      element.classList.add('speaking');
      element.classList.remove('loading');

      element.innerHTML = '';
      const words = text.split(' ');
      let currentIndex = 0;

      // Create word spans with staggered animation
      const wordSpans = words.map((word, index) => {
        const span = document.createElement('span');
        span.className = 'word';
        span.textContent = word + (index < words.length - 1 ? ' ' : '');
        span.style.animationDelay = `${index * 120}ms`;
        return span;
      });

      // Add all word spans at once but they'll animate in sequence
      wordSpans.forEach(span => element.appendChild(span));

      // Remove speaking class when animation completes
      const animationDuration = words.length * 120 + 400; // 120ms delay + 400ms animation per word
      setTimeout(() => {
        element.classList.remove('speaking');
      }, animationDuration);
    },

    // Audio generation and playback methods
    async generateAndPlayCoachAudio(text, language, textElement) {
      // Only log in debug mode - remove for production
      if (window.DEBUG_MODE) {
        console.log('Generating coach audio:', { text, language, voiceKey: this.data.coachData?.voice_key });
      }

      try {
        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (!csrfToken) {
          throw new Error('CSRF token not found');
        }

        // Create an AbortController with timeout
        const controller = this.createAudioRequestController();
        const timeoutId = setTimeout(() => {
          this.markCoachIntroAudioFailed();
          controller.abort('timeout');
        }, 30000);

        let response;
        try {
          // Use the langua endpoint which supports CSRF authentication for web contexts
          response = await fetch('/langua/stream_text_to_speech', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            credentials: 'same-origin',
            signal: controller.signal,
            body: JSON.stringify({
              text: text,
              language: language,
              voice_provider: 'elevenlabs',
              voice: this.data.coachData?.voice_key || 'default',
              // playCoachAudio applies X-Langua-Playback-Rate, so this opts into Eleven v4 (LAN-1390)
              ttsPlaybackRate: true,
              // The coach stays on Eleven v4 whatever the team's Latest | Standard choice (LAN-1654)
              onboarding: true
            })
          });
        } catch (fetchError) {
          throw fetchError;
        } finally {
          clearTimeout(timeoutId);
          this.clearAudioRequestController(controller);
        }


        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to generate audio: ${response.status} - ${errorText}`);
        }

        // Eleven v4 audio has no speed setting: Rails sends the speed tier's rate
        const playbackRate = parseFloat(response.headers.get('X-Langua-Playback-Rate')) || null;

        // Process streaming response

        if (!response.body) {
          throw new Error('Response body is null - cannot read stream');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let audioBytes = "";
        let buffer = "";

        const processChunk = async ({ done, value }) => {

          if (done) {

            if (audioBytes.length === 0) {
              throw new Error('No audio data received from stream');
            }

            // Convert accumulated audioBytes to Uint8Array
            const byteArray = new Uint8Array(audioBytes.length);
            for (let i = 0; i < audioBytes.length; i++) {
              byteArray[i] = audioBytes.charCodeAt(i);
            }

            const audioBlob = new Blob([byteArray.buffer], { type: 'audio/mp3' });
            const audioUrl = URL.createObjectURL(audioBlob);

            await this.playCoachAudio(audioUrl, text, textElement, playbackRate);
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          let lines = buffer.split('\n');

          // Process each complete line as a JSON object
          for (let i = 0; i < lines.length - 1; i++) {
            const jsonString = lines[i].trim();
            if (jsonString) {
              try {
                const responseDict = JSON.parse(jsonString);
                if (responseDict.audio_base64) {
                  const audioBytesChunk = window.atob(responseDict.audio_base64);
                  audioBytes += audioBytesChunk;
                }
              } catch (e) {
                console.error('Error parsing JSON chunk:', e);
              }
            }
          }

          // If the last line is incomplete, keep it in the buffer
          buffer = lines[lines.length - 1].trim();

          return reader.read().then(processChunk);
        };

        await reader.read().then(processChunk);
      } catch (error) {
        if (this.expectedAudioAbort(error)) {
          if (window.DEBUG_MODE) {
            console.debug('Coach audio request aborted');
          }
          return;
        }

        this.markCoachIntroAudioFailed();
        console.error('Error generating coach audio:', error);
        // Show user-friendly error message
        this.showErrorMessage(I18n.t('javascript.onboarding.audio_temporarily_unavailable'));
        // Fallback to text animation
        const greetingEl = document.getElementById('coach-greeting');
        this.animateText(greetingEl, text);
      }
    },

    async playCoachAudio(audioUrl, text, textElement, playbackRate = null) {
      return new Promise((resolve, reject) => {
        // Stop any existing audio
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
        }

        const audio = new Audio();
        this.currentAudio = audio;
        this.applyPlaybackRate(audio, playbackRate);

        // Track if audio actually started playing
        let audioActuallyPlayed = false;

        const playHandler = () => {
          // Start text animation when audio actually starts playing
          if (textElement) {
            // Add speaking class to the entire introduction for avatar effects
            const introElement = document.getElementById('coach-introduction');
            if (introElement) {
              introElement.classList.add('speaking');
            }
            this.animateText(textElement, text);
          }
        };

        const playTrackingHandler = () => {
          audioActuallyPlayed = true;
        };

        const endedHandler = () => {
          // Clean up speaking states
          const introElement = document.getElementById('coach-introduction');
          if (introElement) {
            introElement.classList.remove('speaking');
          }
          if (textElement) {
            textElement.classList.remove('speaking');
          }
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;

          // Only auto-progress if audio actually played (not if it failed to start)
          if (audioActuallyPlayed && this.currentStep === 3 && !this.isNavigatingBackward) {
            this.scheduleAutoProgression(1000, 'coach-audio-completion');
          }

          resolve();
        };

        const errorHandler = (error) => {
          console.error('Audio playback error:', error);
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          reject(error);
        };

        // Store handler references for cleanup
        audio._playHandler = playHandler;
        audio._playTrackingHandler = playTrackingHandler;
        audio._endedHandler = endedHandler;
        audio._errorHandler = errorHandler;

        // Set up all event listeners
        audio.addEventListener('play', playHandler);
        audio.addEventListener('play', playTrackingHandler);
        audio.addEventListener('ended', endedHandler);
        audio.addEventListener('error', errorHandler);

        // Set the source and load
        audio.src = audioUrl;
        audio.load();

        // Try to play once loaded
        audio.addEventListener('canplay', () => {
          audio.play().catch(error => {
            this.markCoachIntroAudioFailed();
            console.warn('Autoplay failed:', error);
            // Don't hide skip button - user should still be able to skip text animation
            // Just show the continue button as an additional option
            const continueBtn = document.getElementById('next-btn');
            if (continueBtn) continueBtn.style.display = 'inline-flex';

            // Clean up the text element loading state and show text animation
            if (textElement) {
              textElement.classList.remove('loading');
              this.animateText(textElement, text);
            }
          });
        }, { once: true });
      });
    },


    playStepVoiceLine() {
      const langLabel = this.getLanguageLabel(this.data.learningLanguage);
      const stepVoiceLines = {
        4: I18n.t('javascript.onboarding.step_voice_lines.level', { language: langLabel }),
        5: I18n.t('javascript.onboarding.step_voice_lines.motivation', { language: langLabel }),
        6: this.data.firstName
          ? I18n.t('javascript.onboarding.step_voice_lines.interests', { firstName: this.data.firstName })
          : I18n.t('javascript.onboarding.step_voice_lines.interests_no_name'),
        7: I18n.t('javascript.onboarding.step_voice_lines.frequency', { language: langLabel }),
        8: I18n.t('javascript.onboarding.step_voice_lines.duration'),
        9: I18n.t('javascript.onboarding.step_voice_lines.source')
      };

      const voiceLineText = stepVoiceLines[this.currentStep];
      if (voiceLineText) {
        // Only the interests line can carry the learner's name; the rest sound
        // the same for everyone, so the server may reuse their audio (LAN-1522).
        const personalised = this.currentStep === 6 && Boolean(this.data.firstName);
        this.generateAndPlayStepAudio(voiceLineText, this.getCoachVoiceLanguage(), { cacheable: !personalised });
      }
    },

    async generateAndPlayStepAudio(text, language, { cacheable = false } = {}) {

      try {
        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (!csrfToken) {
          throw new Error('CSRF token not found');
        }


        // Create an AbortController with timeout
        const controller = this.createAudioRequestController();
        const timeoutId = setTimeout(() => {
          controller.abort('timeout');
        }, 30000);

        let response;
        try {
          // Use the langua endpoint which supports CSRF authentication for web contexts
          response = await fetch('/langua/stream_text_to_speech', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            credentials: 'same-origin',
            signal: controller.signal,
            body: JSON.stringify({
              text: text,
              language: language,
              voice_provider: 'elevenlabs',
              voice: this.data.coachData?.voice_key || 'default',
              // playStepAudio applies X-Langua-Playback-Rate, so this opts into Eleven v4 (LAN-1390)
              ttsPlaybackRate: true,
              // The coach stays on Eleven v4 whatever the team's Latest | Standard choice (LAN-1654)
              onboarding: true,
              // 'default' picks a random voice, so only a known coach voice is cached
              cacheable: cacheable && Boolean(this.data.coachData?.voice_key)
            })
          });
        } catch (fetchError) {
          throw fetchError;
        } finally {
          clearTimeout(timeoutId);
          this.clearAudioRequestController(controller);
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to generate step audio: ${response.status} - ${errorText}`);
        }

        // Eleven v4 audio has no speed setting: Rails sends the speed tier's rate
        const playbackRate = parseFloat(response.headers.get('X-Langua-Playback-Rate')) || null;

        // Process streaming response

        if (!response.body) {
          throw new Error('Step audio response body is null - cannot read stream');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let audioBytes = "";
        let buffer = "";

        const processChunk = async ({ done, value }) => {

          if (done) {

            if (audioBytes.length === 0) {
              throw new Error('No step audio data received from stream');
            }

            // Convert accumulated audioBytes to Uint8Array
            const byteArray = new Uint8Array(audioBytes.length);
            for (let i = 0; i < audioBytes.length; i++) {
              byteArray[i] = audioBytes.charCodeAt(i);
            }

            const audioBlob = new Blob([byteArray.buffer], { type: 'audio/mp3' });
            const audioUrl = URL.createObjectURL(audioBlob);

            await this.playStepAudio(audioUrl, playbackRate);
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          let lines = buffer.split('\n');

          // Process each complete line as a JSON object
          for (let i = 0; i < lines.length - 1; i++) {
            const jsonString = lines[i].trim();
            if (jsonString) {
              try {
                const responseDict = JSON.parse(jsonString);
                if (responseDict.audio_base64) {
                  const audioBytesChunk = window.atob(responseDict.audio_base64);
                  audioBytes += audioBytesChunk;
                }
              } catch (e) {
                console.error('Error parsing step audio JSON chunk:', e);
              }
            }
          }

          // If the last line is incomplete, keep it in the buffer
          buffer = lines[lines.length - 1].trim();

          return reader.read().then(processChunk);
        };

        await reader.read().then(processChunk);
      } catch (error) {
        if (this.expectedAudioAbort(error)) {
          if (window.DEBUG_MODE) {
            console.debug('Step audio request aborted');
          }
          return;
        }

        console.error('Error generating step audio:', error);
        // Show user-friendly error message for step audio failures
        this.showErrorMessage(I18n.t('javascript.onboarding.audio_guidance_unavailable'));
      }
    },

    async playStepAudio(audioUrl, playbackRate = null) {
      return new Promise((resolve, reject) => {
        // Stop any existing audio
        if (this.currentAudio) {
          this.currentAudio.pause();
          this.currentAudio = null;
        }


        const audio = new Audio();
        this.currentAudio = audio;
        this.applyPlaybackRate(audio, playbackRate);

        // Set up all event listeners before setting src



        const endedHandler = () => {
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          resolve();
        };

        audio._endedHandler = endedHandler;
        audio.addEventListener('ended', endedHandler);

        const errorHandler = (error) => {
          console.error('Step audio playback error:', error);
          URL.revokeObjectURL(audioUrl);
          this.currentAudio = null;
          reject(error);
        };

        audio._errorHandler = errorHandler;
        audio.addEventListener('error', errorHandler);

        // Set the source and load
        audio.src = audioUrl;
        audio.load();

        // Try to play once loaded
        audio.addEventListener('canplay', () => {
          audio.play().catch(error => {
            console.error('Failed to play step audio:', error);
            URL.revokeObjectURL(audioUrl);
            this.currentAudio = null;
            reject(error);
          });
        }, { once: true });
      });
    },

    applyPlaybackRate(audio, playbackRate) {
      if (!playbackRate) return;
      // Loading a source resets playbackRate to defaultPlaybackRate, so set both
      audio.defaultPlaybackRate = playbackRate;
      audio.playbackRate = playbackRate;
    },

    getLanguageLabel(langValue) {
      if (!langValue) return I18n.t('javascript.onboarding.this_language', { defaultValue: 'this language' });

      const selectedCard = document.querySelector(`.language-card[data-value="${langValue}"]`);
      const label = selectedCard?.dataset.label || langValue.replace(/_/g, ' ');
      const locale = this.getSiteLocaleCode();

      // Display labels are title-cased, but these labels are interpolated into sentences.
      if (['es', 'fr', 'it', 'pt'].includes(locale)) {
        return label.charAt(0).toLocaleLowerCase(locale) + label.slice(1);
      }

      return label;
    },

    getSiteLocaleCode() {
      return (document.documentElement.lang || 'en').split('-')[0];
    },

    getCoachVoiceLanguage() {
      return this.data.coachData?.language || this.data.learningLanguage;
    },

    getSiteLocale() {
      const locale = this.getSiteLocaleCode();
      // Map ISO locale codes to full language names used by the TTS endpoint
      const localeToLanguage = {
        'en': 'english',
        'es': 'spanish',
        'fr': 'french',
        'de': 'german',
        'it': 'italian',
        'pt': 'portuguese',
        'ja': 'japanese',
        'ko': 'korean',
        'zh': 'chinese',
        'ar': 'arabic',
        'ru': 'russian',
        'nl': 'dutch',
        'pl': 'polish',
        'sv': 'swedish',
        'tr': 'turkish',
        'da': 'danish',
        'fi': 'finnish',
        'el': 'greek',
        'hi': 'hindi',
        'no': 'norwegian',
        'ro': 'romanian',
        'cs': 'czech',
        'hr': 'croatian',
        'th': 'thai'
      };
      return localeToLanguage[locale] || 'english';
    },


    selectNativeLanguage(option) {
      document.querySelectorAll('.native-language-option').forEach(opt => opt.classList.remove('selected'));
      option.classList.add('selected');

      // Check the corresponding radio button
      const radioButton = option.querySelector('input[type="radio"]');
      if (radioButton) {
        radioButton.checked = true;
      }

      this.data.nativeLanguage = option.dataset.value;
      this.updateNextButtonState();

      // Auto-progress after native language selection (like mobile app)
      this.scheduleAutoProgression(300, 'native-language-card');
    },

    async selectMotivation(option) {
      document.querySelectorAll('.motivation-option').forEach(opt => opt.classList.remove('selected'));
      option.classList.add('selected');
      this.data.motivation = option.dataset.value;
      this.updateNextButtonState();

      // Show AI Coach follow-up text and wait for audio to complete
      if (!this.isNavigatingBackward) {
        await this.showMotivationFollowup(option.dataset.value);
        // Auto-progress after audio completes
        this.nextStep();
      } else {
        // If navigating backward, just show the text without audio or auto-progression
        this.showMotivationFollowupText(option.dataset.value);
      }
    },

    async showMotivationFollowup(motivationValue) {
      const followupEl = document.getElementById('ai-coach-followup');
      const followupTextEl = followupEl.querySelector('.followup-text');

      const message = this.getMotivationFollowupMessage(motivationValue);
      if (message) {
        followupTextEl.textContent = message;
        followupEl.style.display = 'block';
        followupEl.style.opacity = '0';
        followupEl.style.animation = 'fadeInUp 0.5s ease forwards';

        // Generate and play audio for the follow-up message using AI coach's voice
        try {
          await this.generateAndPlayStepAudio(message, this.getCoachVoiceLanguage(), { cacheable: true });
        } catch (error) {
          console.error('Failed to play AI coach follow-up audio:', error);
          // Show user-friendly message but continue with progression
          this.showErrorMessage(I18n.t('javascript.onboarding.audio_feedback_unavailable'));
        }
      }
    },

    showMotivationFollowupText(motivationValue) {
      const followupEl = document.getElementById('ai-coach-followup');
      const followupTextEl = followupEl.querySelector('.followup-text');

      const message = this.getMotivationFollowupMessage(motivationValue);
      if (message) {
        followupTextEl.textContent = message;
        followupEl.style.display = 'block';
        followupEl.style.opacity = '0';
        followupEl.style.animation = 'fadeInUp 0.5s ease forwards';
      }
    },

    getMotivationFollowupMessage(motivationValue) {
      if (motivationValue === 'other') return null;
      const langLabel = this.getLanguageLabel(this.data.learningLanguage) || I18n.t('javascript.onboarding.step_voice_lines.level', { language: '' }).trim();
      const key = `javascript.onboarding.motivation_followups.${motivationValue}`;
      const message = I18n.t(key, { language: langLabel });
      // I18n.t returns the key path if translation is missing
      return message !== key ? message : null;
    },

    toggleInterest(tag) {
      const value = tag.dataset.value;
      const isCustom = tag.classList.contains('custom-interest');
      if (isCustom) {
        const idx = this.data.customInterests.indexOf(value);
        if (idx > -1) {
          this.data.customInterests.splice(idx, 1);
          tag.classList.remove('selected');
        } else {
          this.data.customInterests.push(value);
          tag.classList.add('selected');
        }
        this.updateNextButtonState();
        return;
      }

      const index = this.data.interests.indexOf(value);

      if (index > -1) {
        this.data.interests.splice(index, 1);
        tag.classList.remove('selected');
      } else {
        this.data.interests.push(value);
        tag.classList.add('selected');
      }

      this.updateNextButtonState();
    },

    updateCustomInterestButtonState() {
      const input = document.querySelector('.custom-interest-input');
      const addBtn = document.querySelector('.add-interest-btn');
      if (!input || !addBtn) return;

      const hasText = input.value.trim().length > 0;
      addBtn.disabled = !hasText;
      addBtn.classList.toggle('is-muted', !hasText);
    },

    addCustomInterest() {
      const input = document.querySelector('.custom-interest-input');
      if (!input) return;

      const value = input.value.trim();
      this.updateCustomInterestButtonState();

      if (!value) return;

      // Validate input to prevent XSS and ensure quality
      if (!this.isValidCustomInterest(value)) {
        let errorMessage = I18n.t('javascript.onboarding.invalid_interest');
        if (value.length === 0) {
          errorMessage = I18n.t('javascript.onboarding.empty_interest');
        } else if (value.length < 2) {
          errorMessage = I18n.t('javascript.onboarding.interest_too_short');
        } else if (value.length > 50) {
          errorMessage = I18n.t('javascript.onboarding.interest_too_long');
        } else {
          errorMessage = I18n.t('javascript.onboarding.interest_invalid_chars');
        }
        this.showErrorMessage(errorMessage);
        return;
      }

      if (value && !this.data.customInterests.includes(value)) {
        this.data.customInterests.push(value);

        // Create custom interest tag
        const tag = document.createElement('div');
        tag.className = 'interest-tag custom-interest selected';
        tag.dataset.value = value;

        // Create elements safely to prevent XSS
        const icon = document.createElement('span');
        icon.className = 'interest-icon';
        icon.textContent = '📌';

        const label = document.createElement('span');
        label.className = 'interest-label';
        label.textContent = value; // Use textContent to prevent XSS

        const removeBtn = document.createElement('span');
        removeBtn.className = 'remove-interest';
        removeBtn.textContent = '×';

        tag.appendChild(icon);
        tag.appendChild(label);
        tag.appendChild(removeBtn);

        tag.addEventListener('click', () => this.removeCustomInterest(tag));
        document.querySelector('.interests-container').appendChild(tag);

        input.value = '';
        this.updateCustomInterestButtonState();
        this.updateNextButtonState();
      } else if (this.data.customInterests.includes(value)) {
        this.showErrorMessage(I18n.t('javascript.onboarding.interest_already_added'));
      }
    },

    removeCustomInterest(tag) {
      const value = tag.dataset.value;
      const index = this.data.customInterests.indexOf(value);
      if (index > -1) this.data.customInterests.splice(index, 1);
      tag.remove();
      this.updateNextButtonState();
    },

    isValidCustomInterest(value) {
      // Validate custom interest input
      if (!value || value.length === 0) return false;
      if (value.length > 50) return false; // Reasonable length limit
      if (value.length < 2) return false; // Minimum length

      // Prevent HTML/script tags and other potentially dangerous content
      const forbiddenPatterns = [
        /<[^>]*>/,  // HTML tags
        /javascript:/i,
        /vbscript:/i,
        /on\w+\s*=/i,  // Event handlers
        /style\s*=/i
      ];

      for (const pattern of forbiddenPatterns) {
        if (pattern.test(value)) {
          return false;
        }
      }

      return true;
    },

    selectPracticeOption(option) {
      const type = option.dataset.type;
      const value = option.dataset.value;

      // Clear previous selections of the same type
      document.querySelectorAll(`.motivation-option[data-type="${type}"]`).forEach(opt => {
        opt.classList.remove('selected');
      });

      // Select current option
      option.classList.add('selected');

      // Update data
      if (type === 'frequency') {
        this.data.practiceFrequency = value;
      } else if (type === 'duration') {
        this.data.practiceTimeGoal = value;
      } else if (type === 'source') {
        this.data.acquisitionSource = value;
        this.updateNextButtonState();

        // Auto-progress after acquisition source selection (final step)
        if (!this.isNavigatingBackward) {
          setTimeout(() => {
            this.finish();
          }, 300);
        }
        return; // Early return to prevent further processing
      }

      this.updateNextButtonState();

      // Auto-progress after practice option selection (like mobile app)
      if (!this.isNavigatingBackward && this.canProceed()) {
        setTimeout(() => {
          this.nextStep();
        }, 300);
      }
    },

    // Helper function to auto-progress with debouncing
    scheduleAutoProgression(delay = 300, source = 'unknown') {
      // Clear any existing timer
      if (this.autoProgressionTimer) {
        clearTimeout(this.autoProgressionTimer);
        this.autoProgressionTimer = null;
      }

      if (!this.isNavigatingBackward) {
        this.autoProgressionTimer = setTimeout(() => {
          this.autoProgressionTimer = null;
          if (this.currentStep === 3 && source === 'coach-audio-completion') {
            this.coachIntroAdvanceReason = 'audio_complete';
          }
          this.nextStep();
        }, delay);
      }
    },

    // Show user-friendly error messages
    showErrorMessage(message) {
      // Create or get existing error message element
      let errorEl = document.getElementById('onboarding-error-message');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.id = 'onboarding-error-message';
        errorEl.className = 'alert alert-warning mb-4';
        errorEl.style.cssText = `
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
          line-height: 1.4;
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
        `;

        // Add icon
        const icon = document.createElement('span');
        icon.textContent = '⚠️';
        icon.style.fontSize = '16px';
        errorEl.appendChild(icon);

        const textEl = document.createElement('span');
        textEl.className = 'error-text';
        errorEl.appendChild(textEl);

        // Insert at the top of the current step
        const currentStepEl = document.querySelector('.step.active');
        if (currentStepEl) {
          currentStepEl.insertBefore(errorEl, currentStepEl.firstChild);
        }
      }

      // Update message text
      const textEl = errorEl.querySelector('.error-text');
      if (textEl) {
        textEl.textContent = message;
      }

      // Show the message
      errorEl.style.display = 'flex';

      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (errorEl && errorEl.parentNode) {
          errorEl.style.display = 'none';
        }
      }, 5000);
    },

    skipCoachIntroduction() {
      this.stopCurrentAudio();
      if (!this.isNavigatingBackward) {
        this.coachIntroAdvanceReason = 'skip_tapped';
        this.nextStep();
      }
    },

    shuffleAcquisitionSources() {
      const grid = document.getElementById('acquisition-source-grid');
      if (!grid) return;

      // Get all the motivation options
      const options = Array.from(grid.children);

      // Shuffle the options using Fisher-Yates algorithm
      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
      }

      // Clear the grid and append in shuffled order
      grid.innerHTML = '';
      options.forEach(option => {
        grid.appendChild(option);
      });

    },

    setHiddenFieldValue(fieldId, value, { required = false } = {}) {
      const field = document.getElementById(fieldId);
      if (field) {
        field.value = value || '';
        return true;
      }

      if (required) {
        throw new Error(`Required onboarding form field is missing: #${fieldId}`);
      }

      return false;
    },

    finish() {
      if (!this.canProceed()) return;

      this.stopCurrentAudio();

      const form = document.getElementById('onboarding-form');
      if (!form) {
        this.showErrorMessage(I18n.t('javascript.onboarding.form_missing_error'));
        return;
      }

      try {
        // Populate required user field. Team fields are permission-gated in the ERB,
        // so set them only when the current user is allowed to edit the team.
        this.setHiddenFieldValue('form_firstName', this.data.firstName, { required: true });
        this.setHiddenFieldValue('form_stream_language', this.data.learningLanguage);
        this.setHiddenFieldValue('form_learning_languages', this.data.learningLanguage);
        this.setHiddenFieldValue('form_langua_native_language', this.data.nativeLanguage);
        this.setHiddenFieldValue('form_stream_language_level', this.data.languageLevel);
        this.setHiddenFieldValue('form_motivation', this.data.motivation);
        this.setHiddenFieldValue('form_preferred_dialect', this.data.preferredDialect);
        this.setHiddenFieldValue('form_practice_frequency', this.data.practiceFrequency);
        this.setHiddenFieldValue('form_practice_time_goal', this.data.practiceTimeGoal);
        this.setHiddenFieldValue('form_acquisition_source', this.data.acquisitionSource);
      } catch (error) {
        console.error(error);
        this.showErrorMessage(I18n.t('javascript.onboarding.required_field_missing_error'));
        return;
      }

      // Handle interests arrays properly: split predefined vs custom
      const interestsContainer = document.getElementById('interests-hidden-inputs');
      if (interestsContainer) interestsContainer.innerHTML = '';
      // predefined interests → chat_interests[]
      this.data.interests.forEach(interest => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'user[current_team_attributes][chat_interests][]';
        input.value = interest;
        if (interestsContainer) interestsContainer.appendChild(input);
      });
      // custom interests → generated_chat_interests[]
      this.data.customInterests.forEach(interest => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'user[current_team_attributes][generated_chat_interests][]';
        input.value = interest;
        if (interestsContainer) interestsContainer.appendChild(input);
      });

      // Submit the form
      if (this.analytics) this.analytics.trackTerminalCompletion(this, { transport: 'sendBeacon' });
      form.submit();
    }
  };

  onboardingApp.init();

  if (typeof window !== 'undefined') {
    window.__onboardingRevampedApp = onboardingApp;
  }
});
